// @flow
import React, { memo } from "react";
import getSymbolFromCurrency from "currency-symbol-map";
import cx from "classnames";
import t from "common/i18n";
import { getSizeInfoFromBytes } from "utils/fileUtils";
import RadioGroup from "root/components/RadioGroup";
import consts from "../../data/consts";
import type {
	MediaItem, MEDIA_TYPE, PurchaseDetails, Subscription, MediaAssets,
} from "../../data/types";

type Props = {
	className: string,
	size: string,
	purchaseDetails: PurchaseDetails,
	currentSubscription: Subscription,
	asset: MediaItem,
	selectedVariant: ?Object,
	sizeParams: Object,
	format: string,
	mediaType: MEDIA_TYPE,
	changePurchaseDetails: Function,
};

const getIsSizeOptionDisabled = (key, typeSize, allowedSizes, sizeParams) => (!allowedSizes.includes(consts.SIZE_TYPES[key].name || key)
	|| (consts.SIZE_TYPES[key].type === "images"
		? typeSize > sizeParams.maxImageSize
		: typeSize > sizeParams.maxVideoSize));

// only work with SIZE_TYPES we recognize and in the order defined
export const getSizeOptions = (variants: MediaAssets, sizeParams: Object) => Object.keys(consts.SIZE_TYPES)
	.reduce((res, key) => {
		if (variants[key]) {
			res.push({
				value: consts.SIZE_TYPES[key].name || key,
				label: t(`shutterstock.purchase_page.sizes.${key}`),
				disable: consts.SIZE_TYPES[key].type
					&& getIsSizeOptionDisabled(key, variants[key].file_size, sizeParams.allowedSizes, sizeParams),
			});
		}

		return res;
	}, []);

const getIsOverLimit = (asset: MediaItem, mediaType: MEDIA_TYPE, sizeParams) => {
	const limit = mediaType === consts.MEDIA_TYPES.IMAGES
		? sizeParams.maxImageSize
		: sizeParams.maxVideoSize;

	return !!Object.values(asset.assets)
		.find((variant: any) => variant.file_size > limit);
};

const Sizes = (props: Props) => {
	const { selectedVariant } = props;
	const fileSize = selectedVariant && getSizeInfoFromBytes(selectedVariant.file_size);
	const price = props.currentSubscription && props.currentSubscription.price_per_download;
	const isOverLimit = getIsOverLimit(props.asset, props.mediaType, props.sizeParams);

	return <li className={props.className}>
		<h4 className={cx("bg-source-bg-c b mb-4 db")}>{t("shutterstock.purchase_page.format_and_size.label")}</h4>
		<RadioGroup
			showAsBoxes
			options={getSizeOptions(props.asset.assets, props.sizeParams)}
			onChange={(size) => {
				props.changePurchaseDetails({ size });
			}}
			value={props.size}/>

		{selectedVariant && <div className="inline-flex items-center mt4">
			<span className="ttu mr6 o-50">{props.format}</span>
			{fileSize && fileSize && <span className="mr6 o-50">
				{t(`uploader.filesize.${fileSize.text}`, fileSize)}
			</span>}
			{selectedVariant && selectedVariant.width && <span className="o-50">
				{`${selectedVariant.width}x${selectedVariant.height}`}
			</span>}
			{price && <span
				className="b ml6">{`${getSymbolFromCurrency(price.local_currency)}${price.local_amount}`}</span>}
		</div>}

		{isOverLimit
		&& <div className="inline-flex items-center mt4">
            	<span className="mr6 o-50">
            		{t("shutterstock.purchase_page.file_limit_exceeded")}
            	</span>
		</div>}
	</li>;
};

export default memo<Props>(Sizes);
